/*! © 2026 AirX (airx.art). All rights reserved. Unauthorized copying, reproduction, or redistribution of this code is prohibited. Report infringement: info@airx.art */

(function () {
  'use strict';

  if (customElements.get('product-media-gallery')) return;

  const LOUPE_SIZE = 180;
  const LOUPE_ZOOM = 2;

  const motionOn = () =>
    window.gsap && document.documentElement.classList.contains('motion');

  class ProductMediaGallery extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.stage = this.querySelector('[data-media-stage]');
      this.viewer = this.querySelector('canvas-viewer-3d');
      this.modes = this.querySelector('[data-media-modes]');
      this.mode = 'photos';
      this.animating = false;

      this.bindThumbs();
      this.bindModes();
      this.initLoupe();

      if (window.AirX) {
        this.onVariantChange = (e) => {
          const d = e.detail || {};
          if (d.sectionId !== this.sectionId || !d.variant) return;
          if (d.variant.featured_media_id) {
            this.setActiveMedia(String(d.variant.featured_media_id));
          }
        };
        window.AirX.on('variant:change', this.onVariantChange);
      }
    }

    disconnectedCallback() {
      if (window.AirX && this.onVariantChange) {
        window.AirX.off('variant:change', this.onVariantChange);
      }
    }

    activeSlide() {
      return this.stage && this.stage.querySelector('.product-gallery__slide.is-active');
    }

    setActiveMedia(mediaId) {
      if (!this.stage) return;
      const next = this.stage.querySelector(`[data-media-id="${mediaId}"]`);
      const current = this.activeSlide();
      if (!next || next === current || this.animating) return;

      const currentImg = current && current.querySelector('img');
      const nextImg = next.querySelector('img');
      if (currentImg) currentImg.removeAttribute('data-main-product-image');
      if (nextImg) {
        nextImg.setAttribute('data-main-product-image', '');
        nextImg.loading = 'eager';
      }

      this.querySelectorAll('[data-thumb-target]').forEach((thumb) => {
        const active = thumb.dataset.thumbTarget === String(mediaId);
        thumb.classList.toggle('is-active', active);
        if (active) {
          thumb.setAttribute('aria-current', 'true');
          if (thumb.scrollIntoView) {
            thumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
          }
        } else {
          thumb.removeAttribute('aria-current');
        }
      });

      if (motionOn() && current) {
        this.animating = true;
        const gsap = window.gsap;
        next.classList.add('is-active');
        const tl = gsap.timeline({
          onComplete: () => {
            current.classList.remove('is-active');
            gsap.set([current, next, nextImg], { clearProps: 'all' });
            this.animating = false;
            this.emitChange(mediaId);
          },
        });
        tl.to(current, { opacity: 0, scale: 0.985, duration: 0.3, ease: 'power2.in' }, 0);
        tl.fromTo(
          next,
          { opacity: 0 },
          { opacity: 1, duration: 0.45, ease: 'power3.out' },
          0.12
        );
        if (nextImg) {
          tl.fromTo(
            nextImg,
            { scale: 1.06 },
            { scale: 1, duration: 0.9, ease: 'expo.out' },
            0.12
          );
        }
      } else {
        if (current) current.classList.remove('is-active');
        next.classList.add('is-active');
        this.emitChange(mediaId);
      }
    }

    emitChange(mediaId) {
      if (window.AirX) {
        window.AirX.emit('media:change', { sectionId: this.sectionId, mediaId });
      }
    }

    bindThumbs() {
      this.querySelectorAll('[data-thumb-target]').forEach((thumb) => {
        thumb.addEventListener('click', () => {

          if (this.mode === '3d') this.setMode('photos');
          this.setActiveMedia(thumb.dataset.thumbTarget);
        });
      });
    }

    bindModes() {
      this.threeDAvailable = false;
      if (!this.modes) return;

      if (!this.viewer || !window.WebGLRenderingContext) {
        this.modes.hidden = true;
        return;
      }

      this.modes.hidden = false;
      this.threeDAvailable = true;

      this.modes.querySelectorAll('[data-media-mode]').forEach((btn) => {
        btn.addEventListener('click', () => this.setMode(btn.dataset.mediaMode));
        if (btn.dataset.mediaMode === '3d') {
          btn.addEventListener('pointerenter', () => {
            if (this.viewer && this.viewer.preload) this.viewer.preload();
          }, { once: true });
        }
      });

      this.addEventListener('canvas-viewer:unsupported', () => {
        this.threeDAvailable = false;
        this.modes.hidden = true;

        if (this.textLock) this.setTextLock(false);
        this.setMode('photos');
      });
    }

    setTextLock(active) {
      const on = !!active && this.threeDAvailable;
      if (on === !!this.textLock) return;
      this.textLock = on;
      const thumbs = this.querySelector('[data-media-thumbs]');
      if (on) {
        this.setMode('3d');
        if (this.modes) this.modes.hidden = true;
        if (thumbs) thumbs.hidden = true;
      } else {
        if (this.modes) this.modes.hidden = !this.threeDAvailable;
        if (thumbs) thumbs.hidden = false;
        this.setMode('photos');
      }
    }

    setMode(mode) {
      if (mode === this.mode) return;
      this.mode = mode;
      const is3d = mode === '3d';

      this.modes.querySelectorAll('[data-media-mode]').forEach((btn) => {
        const active = btn.dataset.mediaMode === mode;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', String(active));
      });

      if (this.stage) this.stage.hidden = is3d;
      if (this.viewer) {
        this.viewer.hidden = !is3d;
        if (is3d && this.viewer.activate) this.viewer.activate();
        if (!is3d && this.viewer.sleep) this.viewer.sleep();
      }
      this.hideLoupe();
      if (window.AirX) {
        window.AirX.emit('media:mode', { sectionId: this.sectionId, mode });
      }
    }

    initLoupe() {
      if (!this.hasAttribute('data-loupe-enabled')) return;
      if (!this.stage || !window.matchMedia('(pointer: fine)').matches) return;

      this.loupe = document.createElement('div');
      this.loupe.className = 'product-gallery__loupe';
      this.loupe.setAttribute('aria-hidden', 'true');
      this.stage.appendChild(this.loupe);

      this.stage.addEventListener('pointermove', (e) => this.moveLoupe(e));
      this.stage.addEventListener('pointerleave', () => this.hideLoupe());
      this.stage.addEventListener('pointerdown', () => this.hideLoupe());
    }

    moveLoupe(e) {
      if (!this.loupe || this.mode === '3d' || e.pointerType !== 'mouse') return;
      const slide = this.activeSlide();
      const img = slide && slide.querySelector('img');
      if (!img || !img.currentSrc) return this.hideLoupe();

      const rect = img.getBoundingClientRect();
      const inX = e.clientX >= rect.left && e.clientX <= rect.right;
      const inY = e.clientY >= rect.top && e.clientY <= rect.bottom;
      if (!inX || !inY) return this.hideLoupe();

      const stageRect = this.stage.getBoundingClientRect();
      const relX = e.clientX - rect.left;
      const relY = e.clientY - rect.top;

      const src = img.dataset.zoomSrc || img.currentSrc;
      if (this.loupeSrc !== src) {
        this.loupeSrc = src;
        this.loupe.style.backgroundImage = `url("${src}")`;
      }

      this.loupe.style.backgroundSize = `${rect.width * LOUPE_ZOOM}px ${rect.height * LOUPE_ZOOM}px`;
      this.loupe.style.backgroundPosition = `${-(relX * LOUPE_ZOOM - LOUPE_SIZE / 2)}px ${-(relY * LOUPE_ZOOM - LOUPE_SIZE / 2)}px`;
      this.loupe.style.left = `${e.clientX - stageRect.left}px`;
      this.loupe.style.top = `${e.clientY - stageRect.top}px`;
      this.loupe.classList.add('is-active');
    }

    hideLoupe() {
      if (this.loupe) this.loupe.classList.remove('is-active');
    }
  }

  customElements.define('product-media-gallery', ProductMediaGallery);
})();
